import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Trash } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GST_RATES = [0, 5, 12, 18, 28];

/**
 * Editor for existing PURCHASE-side debit/credit notes. Sales notes still
 * go through /notes/:id/edit (NoteEditor.js). Kept as a separate lightweight
 * form because purchase notes reference a purchase (not an invoice) and
 * carry supplier_* fields.
 */
export default function PurchaseNoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("");
  const [notesTxt, setNotesTxt] = useState("");
  const [noteDate, setNoteDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/notes/${id}`);
        const n = res.data;
        setNote(n);
        setItems((n.items || []).map(it => ({
          product_id: it.product_id || "",
          product_name: it.product_name || "",
          hsn_sac: it.hsn_sac || "",
          quantity: Number(it.quantity || 0),
          price: Number(it.price || 0),
          gst_rate: Number(it.gst_rate || 0),
          amount: Number(it.amount || 0),
          unit: (it.unit || "PCS").toUpperCase(),
        })));
        setReason(n.reason || "");
        setNotesTxt(n.notes || "");
        setNoteDate(n.note_date || new Date().toISOString().slice(0, 10));
      } catch (e) {
        alert("Failed to load: " + (e.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
  }, [id]);

  const updateItem = (i, k, v) => setItems(prev => {
    const next = [...prev];
    const numFields = ["quantity", "price", "gst_rate"];
    next[i] = { ...next[i], [k]: numFields.includes(k) ? Number(v) || 0 : (k === "unit" ? String(v).toUpperCase() : v) };
    next[i].amount = Number((next[i].quantity * next[i].price).toFixed(2));
    return next;
  });
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    const isCgstSgst = (note?.gst_type || "cgst_sgst") === "cgst_sgst";
    const taxable = items.reduce((s, i) => s + Number(i.amount || 0), 0);
    let cgst = 0, sgst = 0, igst = 0;
    items.forEach(i => {
      const tax = Number(i.amount || 0) * Number(i.gst_rate || 0) / 100;
      if (isCgstSgst) { cgst += tax / 2; sgst += tax / 2; } else { igst += tax; }
    });
    return { taxable: +taxable.toFixed(2), cgst: +cgst.toFixed(2), sgst: +sgst.toFixed(2), igst: +igst.toFixed(2), total: +(taxable + cgst + sgst + igst).toFixed(2) };
  }, [items, note]);

  const save = async () => {
    if (!reason.trim()) { alert("Reason is mandatory"); return; }
    if (!items.length) { alert("At least one item required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/notes/${id}`, {
        note_type: note.note_type,
        note_date: noteDate,
        reason,
        notes: notesTxt || null,
        gst_type: note.gst_type,
        items,
      });
      alert("Purchase note updated — stock ledger recalculated.");
      navigate("/notes");
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-center text-sm text-slate-500">Loading…</div>;
  if (!note) return <div className="p-10 text-center text-sm text-red-500">Note not found.</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Edit Purchase {note.note_type === "debit" ? "Debit" : "Credit"} Note</h1>
          <p className="text-xs text-slate-500 mt-1">
            {note.note_no} · Supplier: {note.supplier_name} · Ref purchase: {note.ref_purchase_no}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/notes")}>Cancel</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Note Date</Label>
            <Input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} data-testid="edit-note-date" />
          </div>
          <div>
            <Label>GST Type</Label>
            <Input value={note.gst_type === "cgst_sgst" ? "CGST + SGST" : "IGST"} disabled />
          </div>
        </div>
        <div>
          <Label>Reason <span className="text-red-500">*</span></Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="edit-note-reason" />
        </div>

        <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
          <Label className="text-xs font-bold uppercase tracking-wider">Line Items</Label>
          <p className="text-[11px] text-slate-500 mb-2">Unit must match the source purchase bill (KG / PCS / LTR / MTR / …). Stock ledger will re-post on save.</p>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2">
                <Input className="flex-1 min-w-[180px]" value={it.product_name} onChange={(e) => updateItem(idx, "product_name", e.target.value)} />
                <Input className="w-20" value={it.hsn_sac} onChange={(e) => updateItem(idx, "hsn_sac", e.target.value)} placeholder="HSN" />
                <Input className="w-16" type="number" step="0.01" min="0" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`edit-item-qty-${idx}`} />
                <Input className="w-16" value={it.unit || "PCS"} onChange={(e) => updateItem(idx, "unit", e.target.value)} data-testid={`edit-item-unit-${idx}`} title="Unit of measure (must match source bill)" />
                <Input className="w-20" type="number" step="0.01" min="0" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} />
                <select className="w-16 px-1 py-1.5 border border-slate-200 rounded-sm text-xs" value={it.gst_rate} onChange={(e) => updateItem(idx, "gst_rate", Number(e.target.value))}>
                  {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
                <span className="text-sm text-slate-600 min-w-[70px] text-right">₹{Number(it.amount || 0).toFixed(2)}</span>
                <button type="button" onClick={() => removeItem(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded-sm"><Trash size={16} /></button>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-5 gap-2 text-xs">
            <SumCell label="Taxable" val={totals.taxable} />
            <SumCell label="CGST" val={totals.cgst} />
            <SumCell label="SGST" val={totals.sgst} />
            <SumCell label="IGST" val={totals.igst} />
            <SumCell label="Total" val={totals.total} big />
          </div>
        </div>

        <div>
          <Label>Additional Notes (optional)</Label>
          <Textarea rows={2} value={notesTxt} onChange={(e) => setNotesTxt(e.target.value)} />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate("/notes")}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-edited-purchase-note" className={note.note_type === "debit" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const SumCell = ({ label, val, big }) => (
  <div className="text-slate-500">
    <div className="font-semibold">{label}</div>
    <div className={`${big ? "text-base font-black text-emerald-700" : "text-sm font-bold text-slate-900"}`}>₹{Number(val).toFixed(2)}</div>
  </div>
);
